import { useCallback, useEffect, useMemo } from "react";

import { uniqBy } from "lodash";
import {
  StringParam,
  createEnumParam,
  withDefault,
  type QueryParamConfig,
} from "serialize-query-params";
import { useQueryParams } from "use-query-params";

import { useCustomGoalsContext } from "@components/data/custom-goals-provider";
import { useUser } from "@components/user-context";
import { useCustomMetrics } from "@features/custom-metrics/hooks/use-custom-metrics";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { useFeatureFlags } from "@hooks/http";
import { usePrevious } from "@hooks/use-previous";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  makeBenchmarkRows,
  makeFirstTimeVsReturningRows,
  makeOverviewRows,
  makeTrafficRows,
} from "@north-beam/nb-client-side/metrics";
import {
  CustomJsonParam,
  MCAttributionWindowDays,
  NBAccountingMode,
  TimeGranularityV2,
  todayLocalAsISODate,
  type DateInterval,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
  type constants,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import {
  DEFAULT_OVERVIEW_FILTERS,
  compareOverviewFilters,
  parseOverviewFilters,
  type Level,
  type OverviewFilters,
  type OverviewSavedView,
} from "../types";

const AccountingModeParam = createEnumParam<OverviewFilters["accountingMode"]>(
  NBAccountingMode.keyOrder,
);

const AttributionWindowParam = createEnumParam<
  OverviewFilters["attributionWindow"]
>(MCAttributionWindowDays.keyOrder);

const LevelParam = createEnumParam<OverviewFilters["level"]>([
  "campaign",
  "adset",
  "ad",
]);

const TimeGranularityParam = createEnumParam<
  OverviewFilters["timeGranularity"]
>(TimeGranularityV2.keyOrder);

const DateRangeParam = CustomJsonParam<OverviewFilters["dateRange"]>();

const CompareDateRangeParam =
  CustomJsonParam<OverviewFilters["compareDateRange"]>();

export interface FiltersManager {
  readonly filters: OverviewFilters & {
    readonly dateRangeInterval: DateInterval;
    readonly compareDateRangeInterval: DateInterval | null;
  };
  /* We have to keep track of whether or not filters have been saved because they are not
     auto-saved. */
  readonly unsavedFilters: Set<keyof OverviewFilters> | null;
  readonly metrics: SalesPageMetric[];
  readonly isCustomGoalsLoading: boolean;
  setFilters(filters: OverviewFilters): void;
  setAttributionModel(value: string): void;
  setAttributionWindow(value: IMCAttributionWindowDays): void;
  setAccountingMode(value: INBAccountingMode): void;
  setLevel(value: Level): void;
  setTimeGranularity(value: ITimeGranularityV2): void;
  setDateRange(value: FixedDateInterval | DateRangeChoiceV3): void;
  setCompareDateRange(value: constants.CompareDateRange | null): void;
}

export interface UseFiltersConfig {
  readonly selectedSavedView: OverviewSavedView | null;
}

const withFilterDefault = <N extends keyof OverviewFilters>(
  param: QueryParamConfig<OverviewFilters[N], OverviewFilters[N]>,
  name: N,
  selectedSavedView: OverviewSavedView | null,
) =>
  withDefault(
    param,
    selectedSavedView
      ? parseOverviewFilters(selectedSavedView.config.pageConfiguration)[
          name
        ] ?? DEFAULT_OVERVIEW_FILTERS[name]
      : DEFAULT_OVERVIEW_FILTERS[name],
  );

export const useFilters = ({
  selectedSavedView,
}: UseFiltersConfig): FiltersManager => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();

  const isDashboardV2 = hasFeatureFlag("enableDashboardV2");

  const [filters, setFilters] = useQueryParams(
    {
      attributionModel: withDefault(
        StringParam,
        isDashboardV2
          ? "northbeam_custom"
          : selectedSavedView
          ? parseOverviewFilters(selectedSavedView.config.pageConfiguration)
              .attributionModel
          : user.getDefaultAttributionModel(""),
      ),
      attributionWindow: withFilterDefault(
        AttributionWindowParam as QueryParamConfig<
          OverviewFilters["attributionWindow"],
          OverviewFilters["attributionWindow"]
        >,
        "attributionWindow",
        isDashboardV2 ? null : selectedSavedView,
      ),
      accountingMode: withFilterDefault(
        AccountingModeParam as QueryParamConfig<
          OverviewFilters["accountingMode"],
          OverviewFilters["accountingMode"]
        >,
        "accountingMode",
        isDashboardV2 ? null : selectedSavedView,
      ),
      level: withFilterDefault(
        LevelParam as QueryParamConfig<
          OverviewFilters["level"],
          OverviewFilters["level"]
        >,
        "level",
        selectedSavedView,
      ),
      timeGranularity: withFilterDefault(
        TimeGranularityParam as QueryParamConfig<
          OverviewFilters["timeGranularity"],
          OverviewFilters["timeGranularity"]
        >,
        "timeGranularity",
        selectedSavedView,
      ),
      dateRange: withFilterDefault(
        DateRangeParam,
        "dateRange",
        selectedSavedView,
      ),
      compareDateRange: withDefault(CompareDateRangeParam, null),
    },
    { updateType: "pushIn" },
  );

  const viewId = selectedSavedView ? selectedSavedView.id : null;
  const previousSavedViewId = usePrevious(
    selectedSavedView === null ? null : selectedSavedView.id,
  );

  useEffect(() => {
    if (
      viewId &&
      previousSavedViewId &&
      viewId !== previousSavedViewId &&
      selectedSavedView !== null
    ) {
      setFilters(
        parseOverviewFilters(selectedSavedView.config.pageConfiguration),
      );
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [viewId]);

  /* A Set containing the keys representing the filters that have been changed since the last time
     the view was saved. */
  const unsavedFilters = useMemo(() => {
    if (selectedSavedView === null) {
      return null;
    }
    if (selectedSavedView.config.pageConfiguration) {
      return compareOverviewFilters(
        parseOverviewFilters(selectedSavedView.config.pageConfiguration),
        filters,
      );
    }
    return null;
  }, [selectedSavedView, filters]);

  const normalizedAttributionWindow = useMemo(() => {
    if (filters.accountingMode === "cash") {
      return "infinity";
    }

    if (
      filters.accountingMode === "accrual" &&
      filters.attributionWindow === "infinity"
    ) {
      return "1";
    }

    return filters.attributionWindow;
  }, [filters.accountingMode, filters.attributionWindow]);

  const dateRangeInterval = useMemo(
    () => parseDateRangeArgument(filters.dateRange, todayLocalAsISODate()),
    [filters.dateRange],
  );

  const compareDateRangeInterval = useMemo(
    () =>
      filters.compareDateRange &&
      deriveComparisonDateRange(
        dateRangeInterval,
        filters.compareDateRange,
        user.timezone,
        filters.timeGranularity,
      ),
    [
      dateRangeInterval,
      filters.compareDateRange,
      user.timezone,
      filters.timeGranularity,
    ],
  );

  const setAttributionModel = useCallback(
    (value: string) => {
      setFilters({ attributionModel: value });
    },
    [setFilters],
  );

  const setAttributionWindow = useCallback(
    (value: IMCAttributionWindowDays) => {
      setFilters({ attributionWindow: value });
    },
    [setFilters],
  );

  const setAccountingMode = useCallback(
    (value: INBAccountingMode) => {
      setFilters({ accountingMode: value });
    },
    [setFilters],
  );

  const setLevel = useCallback(
    (value: Level) => {
      setFilters({ level: value });
    },
    [setFilters],
  );

  const setTimeGranularity = useCallback(
    (value: ITimeGranularityV2) => {
      setFilters({ timeGranularity: value });
    },
    [setFilters],
  );

  const setDateRange = useCallback(
    (value: FixedDateInterval | DateRangeChoiceV3) => {
      setFilters({ dateRange: value });
    },
    [setFilters],
  );

  const setCompareDateRange = useCallback(
    (value: constants.CompareDateRange | null) => {
      setFilters({ compareDateRange: value });
    },
    [setFilters],
  );

  useEffect(() => {
    if (!previousSavedViewId && selectedSavedView) {
      setCompareDateRange(
        selectedSavedView.config.pageConfiguration.compareDateRange,
      );
    }
  }, [previousSavedViewId, selectedSavedView, setCompareDateRange]);

  const { customGoals, loading: isCustomGoalsLoading } =
    useCustomGoalsContext();
  const { customMetricData } = useCustomMetrics(filters.accountingMode);

  const showV1Forecasts = useMemo(() => {
    return (
      filters.timeGranularity !== "hourly" &&
      filters.attributionModel === "northbeam_custom" &&
      filters.accountingMode === "accrual" &&
      hasFeatureFlag("showV1Forecasts")
    );
  }, [
    hasFeatureFlag,
    filters.accountingMode,
    filters.attributionModel,
    filters.timeGranularity,
  ]);

  const metricsForResolutionById = useMemo(() => {
    const timeGranularity = "daily";

    const metrics = uniqBy(
      [
        ...makeOverviewRows(
          filters.attributionModel,
          normalizedAttributionWindow,
          showV1Forecasts,
          filters.accountingMode,
          customGoals,
          timeGranularity,
          user,
        ),
        ...makeTrafficRows(
          filters.attributionModel,
          normalizedAttributionWindow,
          filters.accountingMode,
          user,
          timeGranularity,
        ),
        ...makeFirstTimeVsReturningRows(
          filters.attributionModel,
          normalizedAttributionWindow,
          filters.accountingMode,
          timeGranularity,
          user.attributionMethodEnum,
        ),
        ...makeBenchmarkRows(
          filters.attributionModel,
          normalizedAttributionWindow,
        ),
        ...customMetricData,
      ].map((metric) => {
        if (metric.metadata?.salesTableMetricId) {
          return {
            ...metric,
            /* This is a temp solution to keep the metric ids the same between different attribution
             windows */
            id: metric.metadata.salesTableMetricId,
          };
        }
        return metric;
      }),
      "id",
    );

    const byId: Record<string, SalesPageMetric> = {};

    for (const metric of metrics) {
      byId[metric.id] = metric;
    }

    return byId;
  }, [
    filters,
    normalizedAttributionWindow,
    user,
    customGoals,
    customMetricData,
    showV1Forecasts,
  ]);

  const metrics = useMemo(() => {
    const resolve = (metric: SalesPageMetric): SalesPageMetric[] => {
      if (metric?.requiredNBCalculatedMetrics) {
        return metric.requiredNBCalculatedMetrics.flatMap((e) =>
          resolve(metricsForResolutionById[e]),
        );
      } else {
        return [metric];
      }
    };

    const metrics = [
      ...makeOverviewRows(
        filters.attributionModel,
        normalizedAttributionWindow,
        showV1Forecasts,
        filters.accountingMode,
        customGoals,
        filters.timeGranularity,
        user,
      ),
      ...makeTrafficRows(
        filters.attributionModel,
        normalizedAttributionWindow,
        filters.accountingMode,
        user,
        filters.timeGranularity,
      ),
      ...makeFirstTimeVsReturningRows(
        filters.attributionModel,
        normalizedAttributionWindow,
        filters.accountingMode,
        filters.timeGranularity,
        user.attributionMethodEnum,
      ),
      ...makeBenchmarkRows(
        filters.attributionModel,
        normalizedAttributionWindow,
      ),
      ...(hasFeatureFlag("customMetricsForOverviewPage")
        ? customMetricData
        : []),
    ].map((metric) => {
      return {
        ...metric,
        id: metric.metadata?.overviewPageMetricId,
        baseMetrics: metric?.requiredNBCalculatedMetrics
          ? resolve(metric)
          : undefined,
      };
    });

    return uniqBy(metrics, "id");
  }, [
    filters,
    normalizedAttributionWindow,
    user,
    customGoals,
    customMetricData,
    showV1Forecasts,
    metricsForResolutionById,
    hasFeatureFlag,
  ]);

  return {
    unsavedFilters,
    filters: {
      ...filters,
      attributionWindow: normalizedAttributionWindow,
      dateRangeInterval,
      compareDateRangeInterval,
    },
    isCustomGoalsLoading,
    metrics,
    setFilters,
    setAttributionModel,
    setAttributionWindow,
    setAccountingMode,
    setLevel,
    setTimeGranularity,
    setDateRange,
    setCompareDateRange,
  };
};
